import { Box, CircularProgress, Grid, MenuItem, TextField } from '@material-ui/core'
import { ExpansionAutocomplete } from 'components/controls/misc/ExpansionAutocomplete'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { getInventoryArticles } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, SortDirection, SortDirectionType, Table } from 'react-virtualized'
import {
  AnyYesNo,
  CardLanguage,
  CARD_LANGUAGES,
  CONDITIONS_MAP_WITH_LABELS,
  convertPTInventoryArticleToPartialArticle,
  Expansion,
  FINISH_TYPES_CONVERSIONS,
  gameMetaData,
  getDisplayLocationName,
  GRADING_COMPANY_LABELS,
  IdGame,
  PTInventoryArticle,
} from 'shared'
import { getProductImageUrl } from 'utils/imageUtils'
import { Filter } from '../stockPricing/FilterDock/Filter'

export interface ArticlesSearchProps {
  userId: undefined | string
  checkedArticle: PTInventoryArticle | undefined
  setCheckedArticle: React.Dispatch<React.SetStateAction<PTInventoryArticle | undefined>>
}

export const ArticlesSearch = ({
  userId,
  checkedArticle,
  setCheckedArticle,
}: ArticlesSearchProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [articlesSearch, setArticlesSearch] = useState<PTInventoryArticle[]>([])
  const tableHeight = Math.min(articlesSearch.length * 45 + 40, 570)
  const [hasSearched, setHasSearched] = useState(false)

  setArticlesSearch

  // filters
  const defaultFilterValues = {
    idGame: 1,
    itemName: '',
    expansionCode: '',
    condition: 'any',
    idLanguage: 'any',
    finishType: AnyYesNo.Any,
    firstEd: AnyYesNo.Any,
    signed: AnyYesNo.Any,
    failed: AnyYesNo.Any,
  }
  const [idGame, setIdGame] = useState<IdGame>()
  const [itemName, setItemName] = useState<string>(defaultFilterValues.itemName)
  const [ids, setIds] = useState({ mkmProductId: '', ptProductId: '', ptArticleId: '' })
  const [condition, setCondition] = useState<string>(defaultFilterValues.condition)
  const [signed, setSigned] = useState<AnyYesNo>(defaultFilterValues.signed)
  const [usEdition, setUsEdition] = useState<AnyYesNo>(AnyYesNo.Any)
  // 'any', 'ungraded' or a grading company code
  const [grading, setGrading] = useState<string>('any')
  const [firstEd, setFirstEd] = useState<AnyYesNo>(defaultFilterValues.firstEd)
  const [finishType, setFinishType] = useState<string>(AnyYesNo.Any)
  const [expansionCode, setExpansionCode] = useState<string>(defaultFilterValues.expansionCode)
  const [idLanguage, setIdLanguage] = useState<CardLanguage | 'any'>(
    defaultFilterValues.idLanguage as CardLanguage | 'any'
  )
  const [sortBy, setSortBy] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.DESC)

  const getYesNoAnyValue = (field: AnyYesNo) => {
    if (field === AnyYesNo.Any) {
      return undefined
    } else if (field === AnyYesNo.Yes) {
      return true
    } else {
      return false
    }
  }

  const anyYesNoOptions = [
    <MenuItem key={'any'} value={AnyYesNo.Any}>
      {t('filters.any')}
    </MenuItem>,
    <MenuItem key={'false'} value={AnyYesNo.No}>
      {t('filters.no')}
    </MenuItem>,
    <MenuItem key={'true'} value={AnyYesNo.Yes}>
      {t('filters.yes')}
    </MenuItem>,
  ]

  const allExpansionsOption = {
    name: t('addArticles.allExpansions'),
    code: '',
    _id: 0,
  } as Expansion

  const searchArticles = async () => {
    if (!userId) {
      enqueueSnackbar('select user before search articles', { variant: 'error' })
      return
    }

    setLoading(true)

    const params = {
      userId: userId,
      idGame: idGame,
      cardName: itemName,
      expansion: expansionCode === '' ? undefined : expansionCode,
      language: idLanguage === AnyYesNo.Any ? undefined : idLanguage,
      condition: condition === 'any' ? undefined : condition,
      finishType:
        finishType === 'any'
          ? undefined
          : FINISH_TYPES_CONVERSIONS.find((entry) => entry.label === finishType)!.code,
      firstEdition: getYesNoAnyValue(firstEd),
      signed: getYesNoAnyValue(signed),
      usEdition: getYesNoAnyValue(usEdition),
      grading: grading === 'any' ? undefined : grading,
      mkmProductId: ids.mkmProductId.trim() || undefined,
      ptProductId: ids.ptProductId.trim() || undefined,
      ptArticleId: ids.ptArticleId.trim() || undefined,
    }
    const results = await getInventoryArticles(params)
    setArticlesSearch(results)
    setHasSearched(true)
    setLoading(false)
  }

  const sort = ({ sortBy }: { sortBy: string }) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC)
    let sortedOrders = [...articlesSearch]

    if (sortBy === 'name') {
      sortedOrders = articlesSearch.sort((a, b) =>
        (b.ptProduct.mkmProduct?.name ?? '').localeCompare(a.ptProduct.mkmProduct?.name ?? '')
      )
    } else if (sortBy === 'price') {
      // The price can come as a decimal string: compare it as a number
      sortedOrders = articlesSearch.sort((a, b) => {
        return Number(a.baseSellPrice) < Number(b.baseSellPrice) ? 1 : -1
      })
    } else if (sortBy === 'comment') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return a.comment < b.comment ? 1 : -1
      })
    } else if (sortBy === 'ptProductId') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return Number(a.ptProduct._id) < Number(b.ptProduct._id) ? 1 : -1
      })
    } else if (sortBy === 'mkmProductId') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return a.ptProduct.mkmProductId < b.ptProduct.mkmProductId ? 1 : -1
      })
    } else if (sortBy === 'ptArticleId') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return Number(a._id) < Number(b._id) ? 1 : -1
      })
    } else if (sortBy === 'locationName') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return a.locations[0].name < b.locations[0].name ? 1 : -1
      })
    } else if (sortBy === 'locationQuantity') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return a.locations[0].quantity < b.locations[0].quantity ? 1 : -1
      })
    } else if (sortBy === 'createdAt') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return (a.createdAt ?? '') < (b.createdAt ?? '') ? 1 : -1
      })
    } else if (sortBy === 'reservedQuantity') {
      sortedOrders = articlesSearch.sort((a, b) => {
        return a.reservedQuantity < b.reservedQuantity ? 1 : -1
      })
    }

    if (sortDirection === SortDirection.DESC) {
      sortedOrders = sortedOrders.reverse()
    }
    return sortedOrders
  }

  const toggleArticle = (article: PTInventoryArticle) => {
    setCheckedArticle(checkedArticle === article ? undefined : article)
  }

  return (
    <>
      <div className="article-section mb-5">
        <h4 className="my-4">Articles</h4>
        <div>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                type="select"
                menuitems={gameMetaData.map((game) => (
                  <MenuItem key={game.idGame} value={game.idGame} data-testid={`${game.name}-game`}>
                    <span>{game.name}</span>
                  </MenuItem>
                ))}
                setFilter={(_name, newValue) => setIdGame(newValue)}
                defaultValue={idGame}
                label={t('common.game')}
                name={'game'}
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
              <Box marginTop="11px">
                <ExpansionAutocomplete
                  data-testid="expansion-filter"
                  value={expansionCode}
                  options={[allExpansionsOption]}
                  onChange={(e) => setExpansionCode(e.code)}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={4} lg={3} xl={3}>
              <TextField
                data-testid="item-name-filter"
                type="text"
                label={t('backoffice.searchCardName')}
                fullWidth
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    searchArticles()
                  }
                }}
              />
            </Grid>

            {(
              [
                ['mkmProductId', t('backoffice.mkmProductId')],
                ['ptProductId', t('backoffice.ptProductId')],
                ['ptArticleId', 'PtArticleId'],
              ] as const
            ).map(([key, label]) => (
              <Grid key={key} item xs={4} sm={2} md={2} lg={1} xl={1}>
                <TextField
                  label={label}
                  fullWidth
                  value={ids[key]}
                  inputProps={{ inputMode: 'numeric' }}
                  // ids only: anything else would reach Postgres as NaN
                  onChange={(e) => setIds({ ...ids, [key]: e.target.value.replace(/\D/g, '') })}
                  onKeyDown={(e) => e.key === 'Enter' && searchArticles()}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={3} className="mt-1">
            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                data-testid="language-filter"
                type="select"
                menuitems={[
                  <MenuItem key={'any'} value={'any'}>
                    {t('filters.any')}
                  </MenuItem>,

                  ...CARD_LANGUAGES.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  )),
                ]}
                defaultValue={idLanguage}
                setFilter={(_name, newValue) => setIdLanguage(newValue)}
                label={t('card.language')}
                name="language"
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                type="select"
                defaultValue={condition === '' ? 'any' : condition}
                setFilter={(_name, newValue) => setCondition(newValue)}
                label={t('common.condition')}
                name="condition"
                menuitems={[
                  <MenuItem key={'any'} value={'any'}>
                    {t('filters.any')}
                  </MenuItem>,
                  ...CONDITIONS_MAP_WITH_LABELS.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name}
                    </MenuItem>
                  )),
                ]}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                data-testid="finishType-filter"
                type="select"
                menuitems={[
                  <MenuItem key={'any'} value={'any'}>
                    {t('filters.any')}
                  </MenuItem>,
                  ...FINISH_TYPES_CONVERSIONS.map((finishType) => (
                    <MenuItem key={finishType.label} value={finishType.label}>
                      {finishType.label}
                    </MenuItem>
                  )),
                ]}
                setFilter={(_name, newValue) => setFinishType(newValue)}
                defaultValue={finishType}
                label={t(`filters.finishType`)}
                name={'finishType'}
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                type="select"
                menuitems={anyYesNoOptions}
                setFilter={(_name, newValue) => setFirstEd(newValue)}
                defaultValue={firstEd}
                label={t(`filters.firstEd`)}
                name={'firstEd'}
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                type="select"
                menuitems={anyYesNoOptions}
                setFilter={(_name, newValue) => setSigned(newValue)}
                defaultValue={signed}
                label={t(`filters.signed`)}
                name={'signed'}
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                type="select"
                menuitems={anyYesNoOptions}
                setFilter={(_name, newValue) => setUsEdition(newValue)}
                defaultValue={usEdition}
                label={t('filters.americanEdition')}
                name={'usEdition'}
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Filter
                type="select"
                menuitems={[
                  <MenuItem key="any" value="any">
                    {t('filters.any')}
                  </MenuItem>,
                  <MenuItem key="ungraded" value="ungraded">
                    {t('backoffice.notGraded')}
                  </MenuItem>,
                  ...GRADING_COMPANY_LABELS.map(({ code }) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  )),
                ]}
                setFilter={(_name, newValue) => setGrading(newValue)}
                defaultValue={grading}
                label={t('stockPricing.gradingCompany')}
                name={'grading'}
                activeFilter={false}
              />
            </Grid>

            <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
              <Button
                data-testid="search-btn"
                fullWidth
                variant="contained"
                onClick={() => searchArticles()}
                disabled={!userId}
              >
                Search articles
              </Button>
            </Grid>
          </Grid>
        </div>

        {loading ? (
          <div className="w-100 d-flex flex-column align-items-center ">
            <h6 className="pb-5">Loading</h6>
            <CircularProgress size={100} />
          </div>
        ) : articlesSearch.length === 0 ? (
          hasSearched && (
            <span className="my-5 d-block text-center fw-bold" style={{ fontSize: '1.1rem' }}>
              No search results found
            </span>
          )
        ) : (
          <>
            <div
              className="article-table-results my-5"
              style={{
                // the table plus the rows count above it
                height: tableHeight + 30,
              }}
            >
              <span className="mb-1">{articlesSearch.length} rows</span>
              <AutoSizer>
                {({ width }) => (
                  <Table
                    width={Math.max(width, 1300)}
                    height={tableHeight}
                    headerHeight={40}
                    rowHeight={45}
                    rowCount={articlesSearch.length}
                    rowGetter={({ index }) => articlesSearch[index]}
                    className={`articles-results-table`}
                    sort={sort}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onRowClick={({ rowData }: { rowData: PTInventoryArticle }) =>
                      toggleArticle(rowData)
                    }
                    rowStyle={({ index }: { index: number }) => ({
                      cursor: 'pointer',
                      backgroundColor:
                        index >= 0 && articlesSearch[index] === checkedArticle
                          ? '#e3f2fd'
                          : undefined,
                    })}
                  >
                    <Column
                      label=""
                      dataKey=""
                      width={70}
                      flexGrow={0}
                      flexShrink={0}
                      disableSort={true}
                      cellRenderer={({ rowData: article }) => (
                        <ImagePopover
                          variant="iconPopover"
                          imageComponent={
                            <img
                              width="62px"
                              src={getProductImageUrl(article.ptProduct.mkmProductId.toString())}
                            />
                          }
                          imageId={article.ptProduct.mkmProductId.toString()}
                        />
                      )}
                    />

                    <Column
                      width={120}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('backoffice.ptProductId')}
                      dataKey="ptProductId"
                      cellRenderer={({ rowData: article }) => <div>{article.ptProduct._id}</div>}
                    />

                    <Column
                      width={120}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('backoffice.mkmProductId')}
                      dataKey="mkmProductId"
                      cellRenderer={({ rowData: article }) => {
                        const game = gameMetaData.find(
                          (g) => g.idGame === article.ptProduct.mkmProduct?.idGame
                        )
                        if (!game) return <div>{article.ptProduct.mkmProductId}</div>
                        const url = `https://www.cardmarket.com/en/${
                          game.cardmarketName || game.name
                        }/Products?idProduct=${article.ptProduct.mkmProductId}`
                        return (
                          <a
                            href={url}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              window.open(url, '', 'width=600,height=640')
                            }}
                          >
                            {article.ptProduct.mkmProductId}
                          </a>
                        )
                      }}
                    />

                    <Column
                      width={120}
                      flexGrow={0}
                      flexShrink={0}
                      label="PtArticleId"
                      dataKey="ptArticleId"
                      cellRenderer={({ rowData: article }) => <div>{article._id}</div>}
                    />

                    <Column
                      width={220}
                      flexGrow={2}
                      flexShrink={1}
                      label={t('common.name')}
                      dataKey="name"
                      cellRenderer={({ rowData: article }) => (
                        <div className="d-flex flex-column">
                          <span>{article.ptProduct.mkmProduct.name}</span>
                          <span>
                            {article.ptProduct.mkmProduct.cn} -{' '}
                            {article.ptProduct.mkmProduct.expansion.code}
                          </span>
                        </div>
                      )}
                    />

                    <Column
                      width={230}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('common.properties')}
                      dataKey="properties"
                      disableSort
                      cellRenderer={({ rowData: article, rowIndex }) => (
                        <div>
                          <ArticleAttributes
                            article={convertPTInventoryArticleToPartialArticle(article)}
                            identifierForDataTestId={rowIndex.toString()}
                            iconHeight={20}
                          />
                        </div>
                      )}
                    />

                    <Column
                      width={100}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('common.price')}
                      dataKey="price"
                      cellRenderer={({ rowData: article }) => <div>{article.baseSellPrice}</div>}
                    />

                    <Column
                      width={200}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('common.comment')}
                      dataKey="comment"
                      cellRenderer={({ rowData: article }) => (
                        <div title={article.comment}>{article.comment}</div>
                      )}
                    />

                    <Column
                      width={200}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backoffice.locationName')}
                      dataKey="locationName"
                      cellRenderer={({ rowData: article }) => (
                        <div> {getDisplayLocationName(article.locations[0].name)}</div>
                      )}
                    />

                    <Column
                      width={120}
                      flexGrow={0}
                      flexShrink={0}
                      label={t('backoffice.locationId')}
                      dataKey="locationId"
                      disableSort
                      cellRenderer={({ rowData: article }) => <div>{article.locations[0]._id}</div>}
                    />

                    <Column
                      width={100}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backoffice.locationQty')}
                      dataKey="locationQuantity"
                      cellRenderer={({ rowData: article }) => (
                        <div>{article.locations[0].quantity}</div>
                      )}
                    />

                    <Column
                      width={100}
                      flexGrow={1}
                      flexShrink={1}
                      label={t('backoffice.reservedQty')}
                      dataKey="reservedQuantity"
                      cellRenderer={({ rowData: article }) => <div>{article.reservedQuantity}</div>}
                    />

                    <Column
                      width={180}
                      flexGrow={1}
                      flexShrink={1}
                      label="Created at"
                      dataKey="createdAt"
                      cellRenderer={({ rowData: article }) => (
                        <div>
                          {article.createdAt && new Date(article.createdAt).toLocaleString()}
                        </div>
                      )}
                    />
                  </Table>
                )}
              </AutoSizer>
            </div>
          </>
        )}
      </div>
    </>
  )
}
